import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LabelElement, Position } from '@/types'
import { generateId } from '@/lib/utils/generateId'

interface HistoryState {
  past: LabelElement[][]
  future: LabelElement[][]
}

interface ResizeParams {
  width?: number
  height?: number
  diameter?: number
}

interface ElementsState {
  elements: LabelElement[]
  selectedId: string | null
  selectedIds: string[] // Multi-selection support
  history: HistoryState
  clipboard: LabelElement[] // Clipboard for copy/paste

  // Element operations
  addElement: (element: LabelElement) => void
  updateElement: (id: string, updates: Partial<LabelElement>) => void
  updateElements: (ids: string[], updates: Partial<LabelElement>) => void // Batch update
  deleteElement: (id: string) => void
  deleteElements: (ids: string[]) => void // Batch delete
  moveElement: (id: string, position: Position) => void
  moveElements: (ids: string[], deltaX: number, deltaY: number) => void // Move multiple elements by delta
  duplicateElement: (id: string) => void
  resizeElement: (id: string, params: ResizeParams) => void

  // Selection
  selectElement: (id: string | null) => void
  toggleSelectElement: (id: string) => void // Toggle for multi-select
  selectElements: (ids: string[]) => void // Select multiple
  addToSelection: (id: string) => void // Add to existing selection
  clearSelection: () => void
  getSelectedElement: () => LabelElement | undefined
  getSelectedElements: () => LabelElement[]

  // History
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean

  // Bulk operations
  clearElements: () => void
  setElements: (elements: LabelElement[]) => void

  // Clipboard operations
  copySelectedElements: () => void
  pasteElements: (offsetX?: number, offsetY?: number) => void
  hasClipboard: () => boolean

  // Layer operations (z-index)
  bringToFront: (id: string) => void
  sendToBack: (id: string) => void
  bringForward: (id: string) => void
  sendBackward: (id: string) => void
}

export const useElementsStore = create<ElementsState>()(
  persist(
    (set, get) => ({
      elements: [],
      selectedId: null,
      selectedIds: [],
      history: {
        past: [],
        future: [],
      },
      clipboard: [],

  addElement: (element) => {
    const { elements, history } = get()
    set({
      elements: [...elements, element],
      selectedId: element.id,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  updateElement: (id, updates) => {
    const { elements, history } = get()
    set({
      elements: elements.map((el) =>
        el.id === id ? { ...el, ...updates } as LabelElement : el
      ),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  updateElements: (ids, updates) => {
    const { elements, history } = get()
    set({
      elements: elements.map((el) =>
        ids.includes(el.id) ? { ...el, ...updates } as LabelElement : el
      ),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  deleteElement: (id) => {
    const { elements, selectedId, selectedIds, history } = get()
    set({
      elements: elements.filter((el) => el.id !== id),
      selectedId: selectedId === id ? null : selectedId,
      selectedIds: selectedIds.filter((sid) => sid !== id),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  deleteElements: (ids) => {
    const { elements, selectedId, history } = get()
    set({
      elements: elements.filter((el) => !ids.includes(el.id)),
      selectedId: selectedId && ids.includes(selectedId) ? null : selectedId,
      selectedIds: [],
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  moveElement: (id, position) => {
    const { elements, history } = get()
    set({
      elements: elements.map((el) =>
        el.id === id ? { ...el, position } : el
      ),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  moveElements: (ids, deltaX, deltaY) => {
    const { elements, history } = get()
    set({
      elements: elements.map((el) =>
        ids.includes(el.id)
          ? {
              ...el,
              position: {
                x: Math.max(0, el.position.x + deltaX),
                y: Math.max(0, el.position.y + deltaY),
              },
            }
          : el
      ),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  duplicateElement: (id) => {
    const { elements, history } = get()
    const element = elements.find((el) => el.id === id)
    if (!element) return

    const newElement = {
      ...element,
      id: generateId(),
      position: {
        x: element.position.x + 5,
        y: element.position.y + 5,
      },
    }

    set({
      elements: [...elements, newElement],
      selectedId: newElement.id,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  resizeElement: (id, params) => {
    const { elements, history } = get()
    set({
      elements: elements.map((el) => {
        if (el.id !== id) return el

        // Apply size changes based on element type
        const updates: Record<string, number> = {}

        if (params.width !== undefined && 'width' in el) {
          updates.width = Math.max(1, params.width)
        }
        if (params.height !== undefined && 'height' in el) {
          updates.height = Math.max(1, params.height)
        }
        if (params.diameter !== undefined && 'diameter' in el) {
          updates.diameter = Math.max(1, params.diameter)
        }

        return { ...el, ...updates } as LabelElement
      }),
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  selectElement: (id) => set({
    selectedId: id,
    selectedIds: id ? [id] : [],
  }),

  toggleSelectElement: (id) => {
    const { selectedIds } = get()
    if (selectedIds.includes(id)) {
      const newSelectedIds = selectedIds.filter((sid) => sid !== id)
      set({
        selectedIds: newSelectedIds,
        selectedId: newSelectedIds.length > 0 ? newSelectedIds[newSelectedIds.length - 1] : null,
      })
    } else {
      set({
        selectedIds: [...selectedIds, id],
        selectedId: id,
      })
    }
  },

  selectElements: (ids) => set({
    selectedIds: ids,
    selectedId: ids.length > 0 ? ids[ids.length - 1] : null,
  }),

  addToSelection: (id) => {
    const { selectedIds } = get()
    if (!selectedIds.includes(id)) {
      set({
        selectedIds: [...selectedIds, id],
        selectedId: id,
      })
    }
  },

  clearSelection: () => set({
    selectedId: null,
    selectedIds: [],
  }),

  getSelectedElement: () => {
    const { elements, selectedId } = get()
    return elements.find((el) => el.id === selectedId)
  },

  getSelectedElements: () => {
    const { elements, selectedIds } = get()
    return elements.filter((el) => selectedIds.includes(el.id))
  },

  undo: () => {
    const { history, elements } = get()
    if (history.past.length === 0) return

    const previous = history.past[history.past.length - 1]
    const newPast = history.past.slice(0, -1)

    set({
      elements: previous,
      history: {
        past: newPast,
        future: [elements, ...history.future],
      },
    })
  },

  redo: () => {
    const { history, elements } = get()
    if (history.future.length === 0) return

    const next = history.future[0]
    const newFuture = history.future.slice(1)

    set({
      elements: next,
      history: {
        past: [...history.past, elements],
        future: newFuture,
      },
    })
  },

  canUndo: () => get().history.past.length > 0,
  canRedo: () => get().history.future.length > 0,

  clearElements: () => {
    const { elements, history } = get()
    set({
      elements: [],
      selectedId: null,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  setElements: (elements) => {
    const { elements: currentElements, history } = get()
    set({
      elements,
      selectedId: null,
      history: {
        past: [...history.past, currentElements],
        future: [],
      },
    })
  },

  copySelectedElements: () => {
    const { elements, selectedIds } = get()
    const selectedElements = elements.filter((el) => selectedIds.includes(el.id))
    if (selectedElements.length > 0) {
      set({ clipboard: selectedElements })
    }
  },

  pasteElements: (offsetX = 3, offsetY = 3) => {
    const { clipboard, elements, history } = get()
    if (clipboard.length === 0) return

    const newElements = clipboard.map((el) => ({
      ...el,
      id: generateId(),
      position: {
        x: el.position.x + offsetX,
        y: el.position.y + offsetY,
      },
    }))

    const newElementIds = newElements.map((el) => el.id)

    set({
      elements: [...elements, ...newElements],
      selectedId: newElementIds[newElementIds.length - 1],
      selectedIds: newElementIds,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  hasClipboard: () => get().clipboard.length > 0,

  bringToFront: (id) => {
    const { elements, history } = get()
    const index = elements.findIndex((el) => el.id === id)
    if (index === -1 || index === elements.length - 1) return

    const element = elements[index]
    const newElements = [...elements.slice(0, index), ...elements.slice(index + 1), element]
    set({
      elements: newElements,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  sendToBack: (id) => {
    const { elements, history } = get()
    const index = elements.findIndex((el) => el.id === id)
    if (index === -1 || index === 0) return

    const element = elements[index]
    const newElements = [element, ...elements.slice(0, index), ...elements.slice(index + 1)]
    set({
      elements: newElements,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  bringForward: (id) => {
    const { elements, history } = get()
    const index = elements.findIndex((el) => el.id === id)
    if (index === -1 || index === elements.length - 1) return

    const newElements = [...elements]
    ;[newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]]
    set({
      elements: newElements,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },

  sendBackward: (id) => {
    const { elements, history } = get()
    const index = elements.findIndex((el) => el.id === id)
    if (index === -1 || index === 0) return

    const newElements = [...elements]
    ;[newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]]
    set({
      elements: newElements,
      history: {
        past: [...history.past, elements],
        future: [],
      },
    })
  },
    }),
    {
      name: 'zpl-elements',
      // Only persist elements, not selection or history
      partialize: (state) => ({ elements: state.elements }),
    }
  )
)
